import React, { useEffect, useState } from 'react';
import { SpacingType } from "@/types";
import { builder, BuilderContent } from "@builder.io/sdk";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from 'next/link';
import { Skeleton } from "@/components/ui/skeleton";
import { PageContext } from '../builder';
import { sectionVariants } from "@/types";
import parse from 'html-react-parser';
import { SectionWrapper } from '../components/SectionWraper';
import { cn, variantBackgroundColors } from '@/lib/utils';

interface ItemData {
  title?: string;
  image?: string;
  url?: string;
}

interface LatestArticlesProps {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  themeChangeOnScroll?: boolean;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  title?: string;
  subtitle?: string;
  readMoreButtonText?: string;
  dataSource?: "case-study" | "market-insights" | "business-guides";
  useContainer?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

export const LatestArticles = ({ 
  spacing = 'xlarge', 
  variant = "A",
  darkMode = false, 
  usedInPageNav = false,
  pageNavTitle = "Blog 3",
  themeChangeOnScroll = false,
  title,
  subtitle,
  readMoreButtonText,
  dataSource,
  attributes 
}: LatestArticlesProps) => {
  const [fetchedItems, setFetchedItems] = useState<BuilderContent[]>([]);
  const [featuredItem, setFeaturedItem] = useState<ItemData | null>(null);
  const [restFeaturedItems, setRestFeaturedItems] = useState<ItemData[]>([]);
  const [readMoreButtonLink, setReadMoreButtonLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const pageFields = React.useContext(PageContext);

  useEffect(() => {
    if (!dataSource) return;
    
    const fetchArticles = async () => {
      try {
        const result = await builder.getAll(dataSource, {
          limit: 6,
          offset: 0,
          options: {
            noTargeting: true,
          },
          preview: true,
          noTraverse: true,
          noEditorUpdates: true,
          prerender: false,
        });

        setFetchedItems(result);
      } catch (err) {
        console.error("Fetch failed", err);
      }
    };
    
    fetchArticles();

    switch (dataSource) {
      case "case-study":
        setReadMoreButtonLink("/resources/case-studies");
        break;
      case "market-insights":
        setReadMoreButtonLink("/resources/market-insights");
        break;
      case "business-guides":
        setReadMoreButtonLink("/resources/business-finance-guides");
        break;
    }
  }, [dataSource, pageFields]);

  useEffect(() => {
    if (!Array.isArray(fetchedItems)) return;

    const items: ItemData[] = [];
    const currentPageUrl = (pageFields?.data as unknown as { currentPage?: { url: string } })?.currentPage?.url;

    fetchedItems.forEach((item) => {
      const url = item?.data?.url ?? '';
      // Skip if URL matches current page
      if (url === currentPageUrl) return;

      const title = item?.data?.title ?? "";
      const image = item?.data?.image ?? "/placeholder.svg";

      items.push({
        title,
        image,
        url,
      });
    });

    if (items.length > 0) {
      setFeaturedItem(items[0]);
      setRestFeaturedItems(items.slice(1, 5));
      setLoading(false);
    }
    
  }, [fetchedItems, pageFields, dataSource]);

  const inputBackgroundColor = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="latest-articles"
      attributes={attributes}
    >
      <div className="container">
        {/* Title Section */}
        <div className="flex justify-between items-center mb-8 lg:mb-16">
          <div className="flex flex-col gap-8">
            {title && (
              <div className="heading-2">
                {parse(title)}
              </div>
            )}
            {subtitle && (
              <div className="mt-2 block text-sm text-muted-foreground md:text-base">
                {parse(subtitle)}
              </div>
            )}
          </div>
          {readMoreButtonText && (
            <Button
              variant="primary"
              size="lg"
              className="ml-auto"
              asChild
            >
              <Link href={readMoreButtonLink ?? '#'}>
                {readMoreButtonText}
              </Link>
            </Button>
          )}
        </div>
        
        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-12">
          {/* Main Featured Post */}
          <div className="flex flex-col gap-6 !cursor-pointer">
            {loading ? (
              <>
                <Skeleton className="aspect-[505/285] xl:h-full w-full rounded-md" />
                <div className="space-y-2">
                  <Skeleton className={cn(inputBackgroundColor, "h-9 w-full")} />
                  <Skeleton className={cn(inputBackgroundColor, "h-9 w-full")} />
                </div>
              </>
            ) : (
              featuredItem && ( 
                <>
                  <Link href={featuredItem?.url ?? '#'} className="relative">
                    <div className="absolute top-0 left-0 w-full h-full">
                      <Image src="/gradient-mask.svg" alt="gradient" width={505} height={285} className="aspect-[505/285] h-full object-fill relative w-full rounded-md z-10" />
                    </div>
                    <Image
                      className="aspect-[505/285] xl:h-full object-cover relative z-1 w-full rounded-md"
                      src={featuredItem?.image ?? '/placeholder.svg'}
                      alt={featuredItem?.title ?? ""}
                      title={featuredItem?.title ?? ""}
                      width={505}
                      height={285}
                    />
                  </Link>
                  <Link href={featuredItem?.url ?? '#'} className="heading-3 !cursor-pointer line-clamp-2">
                    {featuredItem?.title}
                  </Link>
                </>
              )
            )}
          </div>

          {/* Secondary Posts List */}
          <div className="space-y-6 text-foreground md:space-y-6">
            {loading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 border-b pb-6 last:border-b-0">
                  <Skeleton className="w-1/4 shrink-0 md:w-1/5 aspect-[505/285] rounded-md" />
                  <div className="w-3/4 space-y-2">
                    <Skeleton className={cn(inputBackgroundColor, "h-6 w-full")} />
                    <Skeleton className={cn(inputBackgroundColor, "h-6 w-full")} />
                  </div>
                </div>
              ))
            ) : (
              restFeaturedItems.length > 0 && (   
                restFeaturedItems.map((item, index) => (
                  <div
                    key={index}
                  className="flex items-center gap-4 border-b pb-6 last:border-b-0"
                  >
                    <div className="w-1/4 shrink-0 md:w-1/5">
                      <Link href={item.url ?? '#'} className="relative block">
                        <div className="absolute top-0 left-0 w-full h-full">
                          <Image src="/gradient-mask.svg" alt="gradient" width={505} height={285} className="aspect-[505/285] h-full object-fill relative w-full rounded-md z-10" />
                        </div>
                        <Image
                          className="aspect-[505/285] xl:h-full object-cover relative z-1 w-full rounded-md"
                          src={item.image ?? "/placeholder.svg"}
                          alt={item.title ?? ""}
                          title={item.title ?? ""}
                          width={505}
                          height={285}
                        />
                      </Link>
                    </div>
                    <div className="w-3/4 md:w-4/5">
                      {item.title && (
                        <Link href={item.url ?? '#'}>
                          <p className="leading-relaxed paragraph-lg line-clamp-2">
                            {item.title}
                          </p>
                        </Link>
                      )}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}; 

